/**
 * Frontend half of the `pipeline_settings` key vocabulary.
 *
 * The backend owns the key names in `backend/app/queries/pipeline_setting_key.py`
 * (`SettingKey`), and each key's spec (default/type/bounds) in
 * `backend/app/repositories/pipeline_settings_schema.py`. The web bundle cannot
 * import the Python owner, so this module carries a mirror;
 * `backend/tests/test_mirror_parity.py` pins the two together.
 *
 * The admin pages used to re-spell these keys as bare literals — five pages
 * each hardcoded `s.key === 'available_models'` (and re-implemented the
 * comma-splitting of its value), ScoringSettingsPage listed ~28 keys inline,
 * SettingsPage and PipelinePage spelled their own. A typo doesn't fail
 * loudly: `settings.find` just returns undefined and the control silently
 * renders empty. Route every spelling through this module instead.
 */
export const SETTING_KEYS = {
  tldrDecayExponent: 'tldr_decay_exponent',
  scoreTimeDecayOffsetHours: 'score_time_decay_offset_hours',
  scoreDuplicateFirstBoost: 'score_duplicate_first_boost',
  scoreDuplicateSecondBoost: 'score_duplicate_second_boost',
  scoreDuplicateLaterBaseBoost: 'score_duplicate_later_base_boost',
  scoreDuplicateLaterDecay: 'score_duplicate_later_decay',
  scoreTopicFirstBoost: 'score_topic_first_boost',
  scoreTopicSecondBoost: 'score_topic_second_boost',
  scoreTopicLaterBaseBoost: 'score_topic_later_base_boost',
  scoreTopicLaterDecay: 'score_topic_later_decay',
  scoreGatedPenalty: 'score_gated_penalty',
  scoreSourceBoostScore5: 'score_source_boost_score_5',
  scoreSourceBoostScore4: 'score_source_boost_score_4',
  scoreSourcePenaltyScore2: 'score_source_penalty_score_2',
  scoreSourcePenaltyScore1: 'score_source_penalty_score_1',
  scoreSocialHnScoreWeight: 'score_social_hn_score_weight',
  scoreSocialHnCommentsWeight: 'score_social_hn_comments_weight',
  scoreSocialLobstersScoreWeight: 'score_social_lobsters_score_weight',
  scoreSocialLobstersCommentsWeight: 'score_social_lobsters_comments_weight',
  scoreSocialSubstackHeartsWeight: 'score_social_substack_hearts_weight',
  scoreSocialSubstackCommentsWeight: 'score_social_substack_comments_weight',
  scoreSocialGithubStarsWeight: 'score_social_github_stars_weight',
  scoreSocialXLikesWeight: 'score_social_x_likes_weight',
  scoreSocialAlphaxivLikesWeight: 'score_social_alphaxiv_likes_weight',
  scoreSocialTechmemeBoost: 'score_social_techmeme_boost',
  scoreSocialBoostCap: 'score_social_boost_cap',
  scoreSocialPenaltyCap: 'score_social_penalty_cap',
  scoreSocialFloorPenalty: 'score_social_floor_penalty',
  scoreCategorySocialConfigJson: 'score_category_social_config_json',
  smartFeedRelevanceBoostMultiplier: 'smart_feed_relevance_boost_multiplier',
  smartFeedPoolSize: 'smart_feed_pool_size',
  smartFeedManualBatchSize: 'smart_feed_manual_batch_size',
  smartFeedManualFirstPassSize: 'smart_feed_manual_first_pass_size',
  smartFeedJudgeMaxWorkers: 'smart_feed_judge_max_workers',
  smartFeedMinBaseScore: 'smart_feed_min_base_score',
  smartFeedSimilarityMinThreshold: 'smart_feed_similarity_min_threshold',
  rssEntryAgeDays: 'rss_entry_age_days',
  rssAutoFetchMinScore: 'rss_auto_fetch_min_score',
  topicMergeSimilarityThreshold: 'topic_merge_similarity_threshold',
  embeddingDedupSameDomainThreshold: 'embedding_dedup_same_domain_threshold',
  embeddingDedupCrossDomainThreshold: 'embedding_dedup_cross_domain_threshold',
  embeddingDedupAgeGapDays: 'embedding_dedup_age_gap_days',
  embeddingDedupAgeGapFloor: 'embedding_dedup_age_gap_floor',
  embeddingDedupConflictFloor: 'embedding_dedup_conflict_floor',
  embeddingDedupMaxCandidates: 'embedding_dedup_max_candidates',
  topicMergeMaxCandidates: 'topic_merge_max_candidates',
  smartFeedSearchRerankTopN: 'smart_feed_search_rerank_top_n',
  availableModels: 'available_models',
  tldrFeedWindowHours: 'tldr_feed_window_hours',
  tldrFeedExtendedWindowHours: 'tldr_feed_extended_window_hours',
  tldrFeedHolidays: 'tldr_feed_holidays',
  arxivIngestCategories: 'arxiv_ingest_categories',
  fastSearchPrimaryModel: 'fast_search_primary_model',
  fastSearchHedgeModel: 'fast_search_hedge_model',
  fastSearchAuthoritativeCover: 'fast_search_authoritative_cover',
} as const;

export type SettingKey = (typeof SETTING_KEYS)[keyof typeof SETTING_KEYS];

/**
 * The model IDs stored in the `available_models` setting.
 *
 * Owns the knowledge that the setting's value is a comma-separated list
 * (see the backend spec's description) — five admin pages used to re-implement
 * this exact find/split/trim/filter chain. Returns `[]` when the setting is
 * absent or empty.
 */
export function availableModelsFrom(settings: { key: string; value: string }[]): string[] {
  const setting = settings.find((s) => s.key === SETTING_KEYS.availableModels);
  if (!setting?.value) return [];
  return setting.value
    .split(',')
    .map((m) => m.trim())
    .filter(Boolean);
}

/** Serialize a model list back into the `available_models` value shape. */
export function serializeAvailableModels(models: string[]): string {
  return models.join(',');
}
